// src/views/AccommodationProvider/MaintainFloor.js
import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardBody, 
  CardTitle, 
  Badge, 
  Row, 
  Col, 
  Button, 
  Alert,
  Spinner
} from 'reactstrap';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../api/Axios';

const MaintainFloor = () => {
  const navigate = useNavigate();
  const { propertyId, blockId, floorId } = useParams();
  
  // Get provider information from localStorage
  const providerType = localStorage.getItem('provider_type');
  const representativeName = localStorage.getItem('representative_name');
  const regStatus = localStorage.getItem('reg_status');

  const [property, setProperty] = useState(null);
  const [block, setBlock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState({ type: '', message: '', visible: false });
  const [validationErrors, setValidationErrors] = useState({});

  // Form data
  const [formData, setFormData] = useState({
    floor_number: '',
    gender: '',
    no_of_single_rooms: '',
    no_of_shared_rooms: '',
    total_beds: '',
    description: ''
  });

  // Fetch property, block, and floor data
  useEffect(() => {
    const fetchData = async () => {
      if (!propertyId || !blockId || !floorId || regStatus !== '2') {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch property details
        const propertyResponse = await axiosInstance.get(`/api/properties/${propertyId}/`);
        if (propertyResponse.status === 200) {
          setProperty(propertyResponse.data);
        }

        // Fetch block details
        const blockResponse = await axiosInstance.get(`/api/blocks/${blockId}/`);
        if (blockResponse.status === 200) {
          setBlock(blockResponse.data);
        }
        
        // Fetch floor data from API
        try {
          const floorResponse = await axiosInstance.get(`/api/floors/${floorId}/`);
          if (floorResponse.status === 200) {
            const floorData = floorResponse.data;
            setFormData({
              floor_number: floorData.floor_number || '',
              gender: floorData.gender || '',
              no_of_single_rooms: floorData.no_of_single_rooms || '',
              no_of_shared_rooms: floorData.no_of_shared_rooms || '',
              total_beds: floorData.total_beds || '',
              description: floorData.description || ''
            });
          }
        } catch (floorError) {
          console.error('Error fetching floor:', floorError);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setAlert({
          type: 'danger',
          message: 'Failed to load floor data. Please try again.',
          visible: true
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [propertyId, blockId, floorId, regStatus]);

  // Calculate total beds whenever single or shared rooms change
  useEffect(() => {
    const singleRooms = parseInt(formData.no_of_single_rooms) || 0;
    const sharedRooms = parseInt(formData.no_of_shared_rooms) || 0;
    const calculatedBeds = singleRooms + (sharedRooms * 2);
    
    setFormData(prev => ({
      ...prev,
      total_beds: calculatedBeds
    }));
  }, [formData.no_of_single_rooms, formData.no_of_shared_rooms]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleGenderChange = (e) => {
    const selectedValue = e.target.value;
    let genderValue = selectedValue;
    
    // If "inherit" is selected, use the block's gender
    if (selectedValue === 'inherit' && block) {
      genderValue = block.gender;
    }
    
    setFormData(prev => ({
      ...prev,
      gender: genderValue
    }));
    
    // Clear validation error
    if (validationErrors.gender) {
      setValidationErrors(prev => ({
        ...prev,
        gender: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    // Gender validation (required)
    if (!formData.gender) {
      errors.gender = 'Gender designation is required';
    }
    
    // Number of single rooms validation (required and must be >= 0)
    if (formData.no_of_single_rooms === '' || formData.no_of_single_rooms === null) {
      errors.no_of_single_rooms = 'Number of single rooms is required';
    } else if (parseInt(formData.no_of_single_rooms) < 0) {
      errors.no_of_single_rooms = 'Number of single rooms cannot be negative';
    }
    
    // Number of shared rooms validation (required and must be >= 0)
    if (formData.no_of_shared_rooms === '' || formData.no_of_shared_rooms === null) {
      errors.no_of_shared_rooms = 'Number of shared rooms is required';
    } else if (parseInt(formData.no_of_shared_rooms) < 0) {
      errors.no_of_shared_rooms = 'Number of shared rooms cannot be negative';
    }
    
    // At least one room type must be > 0
    const singleRooms = parseInt(formData.no_of_single_rooms) || 0;
    const sharedRooms = parseInt(formData.no_of_shared_rooms) || 0;
    if (singleRooms === 0 && sharedRooms === 0) {
      errors.no_of_single_rooms = 'At least one room type must be greater than 0';
      errors.no_of_shared_rooms = 'At least one room type must be greater than 0';
    }
    
    // Description is optional, no validation needed
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setAlert({
        type: 'danger',
        message: 'Please correct the errors below before submitting.',
        visible: true
      });
      return;
    }

    try {
      setSubmitting(true);
      
      // Update floor via API
      const response = await axiosInstance.put(
        `/api/floors/${floorId}/`,
        {
          gender: formData.gender,
          no_of_single_rooms: formData.no_of_single_rooms,
          no_of_shared_rooms: formData.no_of_shared_rooms,
          total_beds: formData.total_beds,
          description: formData.description,
          is_configured: true,
          block: blockId
        }
      );

      if (response.status === 200) {
        setAlert({
          type: 'success',
          message: 'Floor updated successfully!',
          visible: true
        });
        
        // Redirect back to configure floors after 2 seconds
        setTimeout(() => {
          navigate(`/provider-dashboard/configure-blocks/${propertyId}/block/${blockId}/configure-floors`);
        }, 2000);
      }
    } catch (error) {
      console.error('Error updating floor:', error);
      setAlert({
        type: 'danger',
        message: error.response?.data?.message || 'Failed to update floor. Please try again.',
        visible: true
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/provider-dashboard/configure-blocks/${propertyId}/block/${blockId}/configure-floors`);
  };

  const getWelcomeMessage = () => {
    if (block) {
      return `Maintain Floor - Block ${block.block_number}`;
    }
    return 'Maintain Floor';
  };

  const getProviderTypeDisplay = () => {
    if (providerType === 'individual') {
      return 'Individual Property Owner';
    } else if (providerType === 'entity') {
      return 'Business/Organization';
    }
    return 'Accommodation Provider';
  };

  const getGenderDisplayValue = () => {
    // If floor gender matches block gender, show as "inherit"
    if (block && formData.gender === block.gender) {
      return 'inherit';
    }
    return formData.gender;
  };

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center py-5">
          <Spinner color="primary" />
          <p className="mt-3 text-muted">Loading floor data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Header Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h2 className="mb-1">{getWelcomeMessage()}</h2>
              {property && (
                <p className="text-muted mb-1">{property.trading_name || `Property #${property.id}`}</p>
              )}
              <Badge color="info" className="me-2">
                {getProviderTypeDisplay()}
              </Badge>
              <Badge color="secondary">
                Floor {block?.block_number}{formData.floor_number}
              </Badge>
            </div>
            <Button 
              color="outline-secondary" 
              onClick={handleCancel}
              className="btn-sm"
            >
              <i className="bi bi-arrow-left me-2"></i>
              Back to Floors
            </Button>
          </div>
        </div>
      </div>

      {/* Alert */}
      {alert.visible && (
        <Row className="mb-3">
          <Col>
            <Alert 
              color={alert.type} 
              toggle={() => setAlert({ ...alert, visible: false })}
            >
              {alert.message}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Form */}
      <Row>
        <Col md="12">
          <Card>
            <CardBody>
              <CardTitle tag="h4" className="mb-4">
                <i className="bi bi-layers me-2 text-primary"></i>
                Floor Information
              </CardTitle>
              
              <form onSubmit={handleSubmit} noValidate>
                <Row>
                  <Col md="6">
                    {/* Floor Number */}
                    <div className="form-floating mb-3">
                      <input
                        type="text"
                        className="form-control"
                        id="floor_number"
                        name="floor_number"
                        value={`${block?.block_number || ''}${formData.floor_number}`}
                        placeholder="Floor Number"
                        disabled
                        readOnly
                      />
                      <label htmlFor="floor_number">
                        Floor Number
                      </label>
                      <div className="form-text">System assigned</div>
                    </div>
                  </Col>
                  
                  <Col md="6">
                    {/* Gender */}
                    <div className="form-floating mb-3">
                      <select
                        className={`form-select ${validationErrors.gender ? 'is-invalid' : ''}`}
                        id="gender"
                        name="gender"
                        value={getGenderDisplayValue()}
                        onChange={handleGenderChange}
                        required
                      >
                        <option value="">Select Gender</option>
                        {block && block.gender && (
                          <option value="inherit">
                            Inherit from Block ({block.gender.charAt(0).toUpperCase() + block.gender.slice(1)})
                          </option>
                        )}
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="mixed">Mixed</option>
                      </select>
                      <label htmlFor="gender">
                        Gender Designation <span className="text-danger">*</span>
                      </label>
                      {validationErrors.gender && (
                        <div className="invalid-feedback">
                          {validationErrors.gender}
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    {/* Number of Single Rooms */}
                    <div className="form-floating mb-3">
                      <input
                        type="number"
                        className={`form-control ${validationErrors.no_of_single_rooms ? 'is-invalid' : ''}`}
                        id="no_of_single_rooms"
                        name="no_of_single_rooms"
                        value={formData.no_of_single_rooms}
                        onChange={handleInputChange}
                        placeholder="Number of Single Rooms"
                        min="0"
                        required
                      />
                      <label htmlFor="no_of_single_rooms">
                        Number of Single Rooms <span className="text-danger">*</span>
                      </label>
                      {validationErrors.no_of_single_rooms && (
                        <div className="invalid-feedback">
                          {validationErrors.no_of_single_rooms}
                        </div>
                      )}
                    </div>
                  </Col>

                  <Col md="6">
                    {/* Number of Shared Rooms */}
                    <div className="form-floating mb-3">
                      <input
                        type="number"
                        className={`form-control ${validationErrors.no_of_shared_rooms ? 'is-invalid' : ''}`}
                        id="no_of_shared_rooms"
                        name="no_of_shared_rooms"
                        value={formData.no_of_shared_rooms}
                        onChange={handleInputChange}
                        placeholder="Number of Shared Rooms"
                        min="0"
                        required
                      />
                      <label htmlFor="no_of_shared_rooms">
                        Number of Shared Rooms <span className="text-danger">*</span>
                      </label>
                      {validationErrors.no_of_shared_rooms && (
                        <div className="invalid-feedback">
                          {validationErrors.no_of_shared_rooms}
                        </div>
                      )}
                      <div className="form-text">Shared rooms have 2 beds each</div>
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    {/* Total Beds (Auto-calculated) */}
                    <div className="form-floating mb-3">
                      <input
                        type="number"
                        className="form-control"
                        id="total_beds"
                        name="total_beds"
                        value={formData.total_beds}
                        placeholder="Total Beds"
                        disabled
                        readOnly
                      />
                      <label htmlFor="total_beds">
                        Total Beds
                      </label>
                      <div className="form-text">Auto-calculated (Single rooms × 1 + Shared rooms × 2)</div>
                    </div>
                  </Col>
                </Row>

                {/* Description (Optional) */}
                <div className="form-floating mb-4">
                  <textarea
                    className="form-control"
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Description"
                    style={{ height: '120px' }}
                  />
                  <label htmlFor="description">
                    Description (Optional)
                  </label>
                  <div className="form-text">
                    Additional information about this floor
                  </div>
                </div>

                {/* Form Actions */}
                <div className="d-flex gap-2">
                  <Button 
                    color="primary" 
                    type="submit"
                    disabled={submitting}
                    className="px-4"
                  >
                    {submitting ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-save me-2"></i>
                        Save Changes
                      </>
                    )}
                  </Button>
                  <Button 
                    color="outline-secondary" 
                    type="button"
                    onClick={handleCancel}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardBody>
          </Card>

          {/* Help Card */}
          <Card className="mt-3 border-0 bg-light">
            <CardBody>
              <CardTitle tag="h6" className="mb-3">
                <i className="bi bi-info-circle me-2"></i>
                Field Requirements
              </CardTitle>
              <ul className="small text-muted mb-0">
                <li><strong>Floor Number:</strong> System-assigned identifier (Block + Floor)</li>
                <li><strong>Gender Designation:</strong> Can inherit from block or set explicitly</li>
                <li><strong>Single Rooms:</strong> Each single room has 1 bed</li>
                <li><strong>Shared Rooms:</strong> Each shared room has 2 beds</li>
                <li><strong>Total Beds:</strong> Automatically calculated based on room counts</li>
                <li><strong>Description:</strong> Optional additional information</li>
              </ul>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default MaintainFloor;
